import { useState, useEffect, useRef } from 'react'
import { 
  CheckCircle, 
  Circle, 
  Loader2, 
  XCircle, 
  AlertCircle,
  Bot, 
  FileSearch, 
  Target, 
  Database, 
  PenTool, 
  Image, 
  Scale, 
  Edit3, 
  Package,
  Shield,
  Clock
} from 'lucide-react'
import api from '../lib/api'

const AGENT_ICONS = {
  'Requirements Analyst': FileSearch,
  'Supervisor': Target,
  'Data Injection': Database,
  'Drafting': PenTool,
  'Diagram': Image,
  'Legal Expert': Scale,
  'Editing': Edit3,
  'Export': Package
}

const AGENT_COLORS = {
  'Requirements Analyst': 'blue',
  'Supervisor': 'purple',
  'Data Injection': 'green',
  'Drafting': 'orange',
  'Diagram': 'pink',
  'Legal Expert': 'red',
  'Editing': 'yellow',
  'Export': 'indigo'
}

const AgentProgressTracker = ({ proposalId, onComplete, compact = false }) => {
  const [progress, setProgress] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const pollingRef = useRef(null)

  useEffect(() => {
    if (proposalId) {
      fetchProgress()
      // Poll every 1 second for real-time updates
      pollingRef.current = setInterval(fetchProgress, 1000)
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
      }
    }
  }, [proposalId])

  // Check for stuck state (no progress for 2 minutes with less than 10% progress)
  const isStuck = progress?.generationStatus === 'processing' && 
    progress?.startedAt && 
    (new Date() - new Date(progress.startedAt)) > 2 * 60 * 1000 &&
    progress?.percentage < 10

  // Handle retry
  const handleRetry = async () => {
    try {
      await api.post(`/proposals/${proposalId}/retry`)
      // Refresh progress
      fetchProgress()
    } catch (err) {
      console.error('Retry failed:', err)
    }
  }

  // Handle cancel - use the proper cancel endpoint
  const handleCancel = async () => {
    try {
      await api.post(`/proposals/${proposalId}/cancel`)
      if (onComplete) onComplete()
    } catch (err) {
      console.error('Cancel failed:', err)
    }
  }

  // State for viewing logs
  const [showLogs, setShowLogs] = useState(null) // stage id to show logs for

  useEffect(() => {
    // Stop polling when complete or failed
    if (progress?.generationStatus === 'completed' || progress?.generationStatus === 'failed') {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
      }
      if (onComplete && progress.generationStatus === 'completed') {
        onComplete()
      }
    }
  }, [progress?.generationStatus])

  const fetchProgress = async () => {
    try {
      const response = await api.get(`/proposals/${proposalId}/agent-progress`)
      setProgress(response.data)
      setError(null)
    } catch (err) {
      if (err.response?.status !== 404) {
        setError('Failed to fetch progress')
      }
    } finally {
      setLoading(false)
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />
      case 'running':
        return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />
      default:
        return <Circle className="w-5 h-5 text-gray-300 dark:text-gray-600" />
    }
  }

  const getAgentIcon = (agentName) => {
    const IconComponent = AGENT_ICONS[agentName] || Bot
    return IconComponent
  }

  const getAgentColor = (agentName) => {
    return AGENT_COLORS[agentName] || 'gray'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600 dark:text-gray-400">Loading progress...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-600 dark:text-red-400">
        {error}
      </div>
    )
  }

  if (!progress || !progress.stages?.length) {
    return null
  }

  // Compact view for inline display
  if (compact) {
    // Show stuck state with clear message and actions
    if (isStuck) {
      return (
        <div className="p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-500" />
              <div>
                <p className="text-sm font-medium text-orange-800 dark:text-orange-200">
                  Generation taking longer than expected
                </p>
                <p className="text-xs text-orange-600 dark:text-orange-400">
                  This may be due to API rate limits or high load. You can retry or cancel.
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleCancel}
                className="px-3 py-1 text-sm border border-orange-300 text-orange-700 rounded hover:bg-orange-100 dark:border-orange-700 dark:text-orange-300 dark:hover:bg-orange-900/50"
              >
                Cancel
              </button>
              <button
                onClick={handleRetry}
                className="px-3 py-1 text-sm bg-orange-600 text-white rounded hover:bg-orange-700"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 min-w-0 flex-shrink-0">
          {progress.liveLog?.message ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate max-w-[200px]">
                {progress.liveLog.message}
              </span>
            </>
          ) : progress.currentAgent ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {progress.currentAgent}
              </span>
            </>
          ) : (
            <>
              <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
              <span className="text-sm text-gray-500">Starting generation...</span>
            </>
          )}
        </div>
        <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-300"
            style={{ width: `${Math.max(progress.percentage, 5)}%` }}
          />
        </div>
        <span className="text-sm font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
          {progress.sectionProgress ? `${progress.sectionProgress.current}/${progress.sectionProgress.total}` : `${progress.percentage}%`}
        </span>
      </div>
    )
  }

  // Full view with detailed stages
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">AI Agent Workflow</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {progress.generationStatus === 'completed' 
                ? 'Generation complete' 
                : progress.generationStatus === 'failed'
                ? 'Generation failed'
                : `Stage ${progress.currentStage} of ${progress.totalStages}`}
            </p>
          </div>
        </div>
        
        {/* Overall Progress */}
        <div className="text-right">
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {progress.percentage}%
          </div>
          {progress.estimatedCompletion && progress.generationStatus === 'processing' && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              ~{Math.ceil((new Date(progress.estimatedCompletion) - new Date()) / 60000)} min remaining
            </p>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden mb-6">
        <div 
          className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-green-500 transition-all duration-700 ease-out"
          style={{ width: `${progress.percentage}%` }}
        />
      </div>

      {/* Current Agent Highlight */}
      {progress.currentAgent && progress.generationStatus === 'processing' && (
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-800 rounded-xl flex items-center justify-center">
              {(() => {
                const IconComponent = getAgentIcon(progress.currentAgent)
                return <IconComponent className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              })()}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-blue-900 dark:text-blue-100">
                  {progress.currentAgent} Agent
                </span>
                <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
              </div>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                {progress.stages.find(s => s.status === 'running')?.name || 'Processing...'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stage List */}
      <div className="space-y-3">
        {progress.stages.map((stage, index) => {
          const IconComponent = getAgentIcon(stage.agent)
          const color = getAgentColor(stage.agent)
          const isActive = stage.status === 'running'
          const isComplete = stage.status === 'completed'
          const isFailed = stage.status === 'failed'
          
          return (
            <div key={stage.id} className="space-y-2">
              <div 
                className={`flex items-center gap-4 p-3 rounded-lg transition-all duration-300 ${
                  isActive 
                    ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800' 
                    : isComplete
                    ? 'bg-green-50/50 dark:bg-green-900/10'
                    : isFailed
                    ? 'bg-red-50 dark:bg-red-900/20'
                    : 'bg-gray-50 dark:bg-gray-700/30'
                }`}
              >
              {/* Step Number */}
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                isComplete 
                  ? 'bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300'
                  : isActive
                  ? 'bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-300'
                  : isFailed
                  ? 'bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-300'
                  : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
              }`}>
                {index + 1}
              </div>

              {/* Agent Icon */}
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                isComplete
                  ? 'bg-green-100 dark:bg-green-800'
                  : isActive
                  ? 'bg-blue-100 dark:bg-blue-800'
                  : 'bg-gray-100 dark:bg-gray-700'
              }`}>
                <IconComponent className={`w-5 h-5 ${
                  isComplete
                    ? 'text-green-600 dark:text-green-400'
                    : isActive
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-gray-400 dark:text-gray-500'
                }`} />
              </div>

              {/* Stage Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`font-medium truncate ${
                    isComplete
                      ? 'text-green-700 dark:text-green-300'
                      : isActive
                      ? 'text-blue-700 dark:text-blue-300'
                      : 'text-gray-600 dark:text-gray-400'
                  }`}>
                    {stage.name}
                  </span>
                  {/* Model Badge - show for completed or running stages */}
                  {(isComplete || isActive) && stage.logs?.model && (
                    <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-800 text-purple-700 dark:text-purple-300 text-xs rounded-full font-mono">
                      {stage.logs.model}
                    </span>
                  )}
                  {isActive && (
                    <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-300 text-xs rounded-full">
                      In Progress
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {stage.agent} Agent
                </p>
              </div>

              {/* Status Icon and View Logs */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* View Logs Button - show for completed AND running stages with logs */}
                {(isComplete || isActive) && stage.logs && (
                  <button
                    onClick={() => setShowLogs(showLogs === stage.id ? null : stage.id)}
                    className={`p-1.5 rounded-lg transition-colors ${
                      isActive 
                        ? 'bg-blue-100 dark:bg-blue-800 hover:bg-blue-200 dark:hover:bg-blue-700' 
                        : 'hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                    title={isActive ? "View live agent activity" : "View agent logs"}
                  >
                    <FileSearch className={`w-4 h-4 ${
                      isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'
                    }`} />
                  </button>
                )}
                {getStatusIcon(stage.status)}
              </div>
            </div>
            
              {/* Real-time logs for running stage - always show live status */}
              {isActive && (
                <div className="ml-12 mt-2 space-y-2">
                  {/* Live Activity Status - always visible */}
                  <div className="p-3 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                    {progress?.liveLog ? (
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">{progress.liveLog.message?.split(' ')[0]}</div>
                        <div className="flex-1">
                          <p className="text-blue-800 dark:text-blue-200 font-semibold">
                            {progress.liveLog.message?.substring(progress.liveLog.message.indexOf(' ') + 1)}
                          </p>
                          {progress.liveLog.detail && (
                            <p className="text-blue-600 dark:text-blue-300 text-xs mt-1">
                              {progress.liveLog.detail}
                            </p>
                          )}
                        </div>
                        <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                        <span className="text-blue-700 dark:text-blue-300 font-medium">
                          Initializing agent...
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Detailed Logs Panel - expandable via button */}
                  {showLogs === stage.id && stage.logs && (
                    <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-lg text-xs font-mono">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500 dark:text-gray-400 font-semibold">Agent Input:</span>
                          <span className="text-xs text-green-500 animate-pulse">● Live</span>
                        </div>
                        <pre className="p-2 bg-white dark:bg-gray-800 rounded text-gray-700 dark:text-gray-300 whitespace-pre-wrap overflow-x-auto max-h-32 overflow-y-auto">
                          {stage.logs.input || 'Waiting for input...'}
                        </pre>
                        
                        {/* Raw Log Output - Real-time model activity */}
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500 dark:text-gray-400 font-semibold">Raw Model Log:</span>
                          <span className="text-xs text-yellow-500">Debug</span>
                        </div>
                        <pre className="p-2 bg-black rounded text-green-400 whitespace-pre-wrap overflow-x-auto max-h-40 overflow-y-auto border border-green-800">
{progress?.liveLog?.rawLog || `[${new Date().toISOString()}] Waiting for model output...`}
                        </pre>
                        
                        <div className="text-gray-500 dark:text-gray-400 font-semibold">Agent Status:</div>
                        <div className="p-2 bg-blue-100 dark:bg-blue-800 rounded text-blue-700 dark:text-blue-300">
                          {progress?.liveLog?.message || 'Processing request...'}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Logs Panel - expandable for completed stages */}
              {showLogs === stage.id && stage.logs && !isActive && (
                <div className="ml-12 mt-2 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg text-xs font-mono">
                  <div className="space-y-2">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Input:</span>
                      <pre className="mt-1 p-2 bg-white dark:bg-gray-800 rounded text-gray-700 dark:text-gray-300 whitespace-pre-wrap overflow-x-auto">
                        {stage.logs.input || 'No input data'}
                      </pre>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Output:</span>
                      <pre className="mt-1 p-2 bg-white dark:bg-gray-800 rounded text-green-700 dark:text-green-400 whitespace-pre-wrap overflow-x-auto">
                        {stage.logs.output || 'No output data'}
                      </pre>
                    </div>
                    {stage.logs.duration > 0 && (
                      <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                        <Clock className="w-3 h-3" />
                        <span>Duration: {(stage.logs.duration / 1000).toFixed(2)}s</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Footer */}
      {progress.generationStatus === 'completed' && (
        <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-500" />
            <div>
              <p className="font-semibold text-green-800 dark:text-green-200">
                Proposal Generated Successfully
              </p>
              <p className="text-sm text-green-600 dark:text-green-400">
                All {progress.totalStages} agents completed their tasks
              </p>
            </div>
          </div>
        </div>
      )}

      {progress.generationStatus === 'failed' && (
        <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
          <div className="flex items-center gap-3">
            <XCircle className="w-6 h-6 text-red-500" />
            <div>
              <p className="font-semibold text-red-800 dark:text-red-200">
                Generation Failed
              </p>
              <p className="text-sm text-red-600 dark:text-red-400">
                Please try again or contact support
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AgentProgressTracker
